import { RouteConfig } from '../types';
import { ONBOARDING_KEYS } from '../constants';
import { HELP_CENTER_LINKS } from '../helpCenterLinks';

export const devicesPageSteps = [
  {
    element: '[data-onboarding="devices-page"]',
    popover: {
      title: 'Devices Dashboard',
      description:
        'All devices in your organisation are listed on this page. Search, add devices, and filter by key details.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="devices-add-button"]',
    popover: {
      title: 'Add New Device',
      description:
        `Create a new device by adding a single device, importing cameras from an <a href="${HELP_CENTER_LINKS.nvr}" target="_blank" class="text-lime underline">NVR</a>, or connecting a <a href="${HELP_CENTER_LINKS.vms}" target="_blank" class="text-lime underline">VMS</a>.`,
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="devices-utility-bar"]',
    popover: {
      title: 'Search and Filter Tools',
      description:
        'Use the search bar to find devices by name. Customize your view with column visibility and open the filters panel to narrow results by serial number.',
      side: 'bottom' as const,
      align: 'center' as const,
    },
  },
  {
    element: '[data-onboarding="devices-table"] table tbody, [data-onboarding="devices-table"] table',
    popover: {
      title: 'Devices Table',
      description:
        'All devices are listed with key details like device type, sources, brand/model and customer. Click a device to view its full details.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
];

export const addDeviceSteps = [
  {
    element: '[data-onboarding="add-device-header"]',
    popover: {
      title: 'Add New Device',
      description:
        'Use this page to set up a new device, including its details, location and connections.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-device-sidebar"]',
    popover: {
      title: 'Setup Steps',
      description:
        'The sidebar shows your progress. Complete each step before moving on. The current step is highlighted and finished steps show a tick.',
      side: 'right' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-device-details-section"]',
    popover: {
      title: 'Device Details',
      description:
        'Enter the basic information for this device, such as name, optional serial number, and description, so it is easy to recognise later.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-device-name"]',
    popover: {
      title: 'Device Name',
      description:
        'Choose a clear, descriptive name. This name appears in alerts, reports, and device lists.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-device-serial-number"]',
    popover: {
      title: 'Serial Number',
      description:
        'Optionally add the serial number to help with device tracking and inventory.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-device-location-section"]',
    popover: {
      title: 'Location',
      description:
        'Link this device to a customer and site. You can also set an address or coordinates for its exact location.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-device-customer-select"]',
    popover: {
      title: 'Select Customer',
      description:
        'Choose the customer that owns this device. You can search by name. This links the device to their account.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-device-site-select"]',
    popover: {
      title: 'Select Site',
      description:
        'Choose the site where this device is installed. Sites help group devices by physical location.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-device-connection-section"]',
    popover: {
      title: 'Connection Settings',
      description:
        'Choose the device type (Cloud VMS, NVR/Camera, Speaker/Horn, or Relay), then select the matching model or integration from the list.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="add-device-save-button"]',
    popover: {
      title: 'Save Device',
      description:
        'When required fields are complete and the connection is set, select Save Device to create the device.',
      side: 'top' as const,
      align: 'end' as const,
    },
  },
];

export const deviceEditSteps = [
  {
    element: '[data-onboarding="device-edit-header"]',
    popover: {
      title: 'Device Details',
      description:
        'The device name, integration type, brand, and model are displayed here. The icon indicates the device type, such as NVR, VMS, relay, or speaker.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="device-edit-connection"]',
    popover: {
      title: 'External Connection',
      description:
        'If this device uses an external connection, click this link to view and update its connection settings.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="device-edit-save"]',
    popover: {
      title: 'Save Device',
      description:
        'Shows the last update time and saves changes across all tabs. ',
      side: 'bottom' as const,
      align: 'end' as const,
    },
  },
  {
    element: '[data-onboarding="device-edit-tabs"]',
    popover: {
      title: 'Device Tabs',
      description:
        'Use these tabs to move between different sections of the device configuration and adjust settings for each area.',
      side: 'bottom' as const,
      align: 'center' as const,
    },
  },
  {
    element: '[data-onboarding="device-edit-tab-cameras"]',
    popover: {
      title: 'Cameras and Hardware',
      description:
        'All camera feeds and hardware linked to this device are listed here. Manage cameras, relays, and speakers for videos, control outputs, and audio alerts.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="device-edit-tab-connections"]',
    popover: {
      title: 'Connections',
      description:
        'Set up how this device connects to Watchful. Update connection credentials, authentication, and external connection settings.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="device-edit-tab-settings"]',
    popover: {
      title: 'Settings',
      description:
        'Manage general device details such as name, serial number, description, and map location. You can also access advanced actions like deleting the device.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
    {
    element: '[data-onboarding="device-edit-tab-validation"]',
    popover: {
      title: 'Validation',
      description:
        'Test device configuration to verify it is correctly set up to send alerts to Watchful. View validation results grouped by category with detailed pass/fail status.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
];

export const cameraDetailSteps = [
  {
    element: '[data-onboarding="camera-header"]',
    popover: {
      title: 'Camera Details',
      description:
        'This header shows the camera you are editing. Use the Save button on the right to apply your changes.',
      side: 'bottom' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="camera-grid-editor"]',
    popover: {
      title: 'Motion Detection Grid',
      description:
        'Draw activation zones on the camera view to define where motion should trigger alerts. Leave empty to use the entire frame.',
      side: 'top' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="camera-monitoring-toggle"]',
    popover: {
      title: 'Monitoring Toggle',
      description:
        'Enable or disable monitoring for this camera. When disabled, events from this camera will not create alerts.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="camera-capabilities"]',
    popover: {
      title: 'Capabilities',
      description:
        'Configure what this camera can do: livestreaming, playback, and talkdown. These settings affect how monitoring agents can interact with the feed.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="camera-description"]',
    popover: {
      title: 'Description',
      description:
        "Add a description to help identify this camera's location or purpose.",
      side: 'left' as const,
      align: 'start' as const,
    },
  },
  {
    element: '[data-onboarding="camera-quill-notes"]',
    popover: {
      title: 'Quill Notes',
      description:
        'Provide additional instructions for Quill AI when processing alerts from this camera.',
      side: 'left' as const,
      align: 'start' as const,
    },
  },
];

export const deviceRoutes: RouteConfig[] = [
  {
    id: 'devices',
    processName: ONBOARDING_KEYS.devices,
    match: (path: string) => path === '/devices',
    steps: devicesPageSteps,
  },
  {
    id: 'add-device',
    processName: ONBOARDING_KEYS.addDevice,
    match: (path: string) =>
      path === '/devices/add-device' || /^\/devices\/customer\/[^/]+\/add-device/.test(path),
    steps: addDeviceSteps,
  },
  {
    id: 'device-edit',
    processName: ONBOARDING_KEYS.deviceEdit,
    match: (path: string) => /^\/devices\/[^/]+\/edit$/.test(path),
    steps: deviceEditSteps,
  },
  {
    id: 'camera-detail',
    processName: ONBOARDING_KEYS.cameraDetail,
    match: (path: string) => /^\/devices\/[^/]+\/camera\/[^/]+$/.test(path),
    steps: cameraDetailSteps,
  },
];
